import { SEO } from '../components/SEO';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Cookie Policy - SpinEmerge',
  description: 'Cookie policy for SpinEmerge.com'
};

export const Cookies = () => {
  return (
    <>
      <SEO
        title="Cookie Policy - SpinEmerge"
        description="Learn about how SpinEmerge.com uses cookies and similar tracking technologies to enhance your browsing experience."
        canonical="/cookies"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen pt-20 pb-20">
        <section className="py-12 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-8 text-white">Cookie Policy</h1>
            <p className="text-gray-400 mb-8">Last updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

            <div className="space-y-8 text-gray-300">
              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">1. What Are Cookies?</h2>
                <p className="leading-relaxed">
                  Cookies are small text files that are placed on your computer or mobile device when you visit a website. 
                  They are widely used to make websites work more efficiently and provide information to website owners.
                </p>
              </section>

              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">2. How We Use Cookies</h2>
                <p className="leading-relaxed mb-4">
                  SpinEmerge.com uses cookies for the following purposes:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>To remember your preferences and settings</li>
                  <li>To analyze website traffic and user behavior</li>
                  <li>To improve website functionality and user experience</li>
                  <li>To provide personalized content and advertisements</li>
                  <li>To track affiliate referrals</li>
                </ul>
              </section>

              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">3. Types of Cookies We Use</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">3.1 Essential Cookies</h3>
                    <p className="leading-relaxed">
                      These cookies are necessary for the website to function properly. They enable core functionality 
                      such as security, network management, and accessibility. You cannot opt out of these cookies.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">3.2 Analytics Cookies</h3>
                    <p className="leading-relaxed">
                      These cookies help us understand how visitors interact with our website by collecting and reporting 
                      information anonymously. This helps us improve our website's performance and user experience.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">3.3 Marketing Cookies</h3>
                    <p className="leading-relaxed">
                      These cookies are used to track visitors across websites to display relevant advertisements. They 
                      may also be used to limit the number of times you see an advertisement.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">3.4 Preference Cookies</h3>
                    <p className="leading-relaxed">
                      These cookies remember your choices and preferences to provide a more personalized experience, 
                      such as your language preference or region.
                    </p>
                  </div>
                </div>
              </section>

              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">4. Third-Party Cookies</h2>
                <p className="leading-relaxed mb-4">
                  In addition to our own cookies, we may also use various third-party cookies to report usage statistics 
                  and deliver advertisements. These include:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Google Analytics - for website analytics</li>
                  <li>Affiliate tracking cookies - to track referrals to casino partners</li>
                  <li>Social media cookies - if you interact with social media features</li>
                </ul>
              </section>

              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">5. Managing Cookies</h2>
                <p className="leading-relaxed mb-4">
                  You have the right to accept or reject cookies. Most web browsers automatically accept cookies, but you 
                  can usually modify your browser settings to decline cookies if you prefer.
                </p>
                <p className="leading-relaxed mb-4">
                  However, please note that disabling cookies may affect the functionality of our website and your ability 
                  to access certain features.
                </p>
                <p className="leading-relaxed">
                  You can manage cookie preferences through:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                  <li>Your browser settings (Chrome, Firefox, Safari, Edge, etc.)</li>
                  <li>Our cookie consent banner when you first visit the website</li>
                  <li>Third-party opt-out tools for specific services</li>
                </ul>
              </section>

              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">6. Cookie Duration</h2>
                <p className="leading-relaxed mb-4">
                  Cookies can be either "session" or "persistent" cookies:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Session cookies:</strong> These are temporary cookies that expire when you close your browser</li>
                  <li><strong>Persistent cookies:</strong> These remain on your device for a set period or until you delete them</li>
                </ul>
              </section>

              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">7. Updates to This Policy</h2>
                <p className="leading-relaxed">
                  We may update this Cookie Policy from time to time to reflect changes in our practices or for other 
                  operational, legal, or regulatory reasons. Please revisit this page periodically to stay informed about 
                  our use of cookies.
                </p>
              </section>

              <section className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">8. Contact Us</h2>
                <p className="leading-relaxed">
                  If you have any questions about our use of cookies, please contact us at info@spinemerge.com.
                </p>
              </section>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

